import type { TefComs4csMicroFrontend } from '@mfe-4cs/components/tef-coms4cs';
import { html, TemplateResult, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { ConsentGroupStore } from '@mfe-4cs/state/ConsentGroupStore';
import { comsStore } from '@mfe-4cs/state/ComsStore';
import { hasContent } from '@mfec/lit/hasContent';
import { replaceElements } from '../util';

function getGroupText(this: TefComs4csMicroFrontend, textGroup: ConsentGroupStore): TemplateResult {
  return textGroup.text
    ? html` ${unsafeHTML(replaceElements.bind(this)(textGroup.text))} `
    : nothing as unknown as TemplateResult;
}

// start oneclick checkbox, text and container
function getOneclickHeader(this: TefComs4csMicroFrontend, oGroup: ConsentGroupStore): TemplateResult {
  let labelText = oGroup.text;
  if (oGroup.oneclick && hasContent(oGroup.oneclickScope)) {
    labelText = oGroup.oneclickScope;
  }

  return html`
    <div class="form-group-checkbox">
      <input
        .checked=${oGroup.areAllConsentsAndFeaturesGranted}
        id="group-${oGroup.id}"
        type="checkbox"
        @click=${() => { oGroup.oneClickToggle(); }}
        ?disabled="${comsStore.identity?.readonly ?? false}"/>
      <label for="group-${oGroup.id}">
        ${unsafeHTML(replaceElements.bind(this)(labelText))}
      </label>
    </div>
  `;
}

function getGroupOneclickConsentsContainer(this: TefComs4csMicroFrontend, containerGroup: ConsentGroupStore)
  : TemplateResult {
  const consentTemplate = this.getTemplateFn('consentTemplate');

  const defaultText = 'Wenn ich einzelne Kontaktkanäle und Verarbeitungsvorgänge von meiner Auswahl ausschließen möchte, kann ich hier gesonderte Einwilligungen erteilen.';

  return html`
    <tef-coms-collapsible
      once
      ?is-open="${containerGroup.collapsibleOpen}"
      cloak
      context=${comsStore.config?.styleContext ?? ''}
      id=${containerGroup.name}
    >
      <div slot="title">
        ${containerGroup.oneclickCollapsible ? unsafeHTML(containerGroup.oneclickCollapsible) : defaultText}
      </div>
      <div slot="content">
        <!-- consentPrefix -->
        ${containerGroup.consentPrefix ? html`${unsafeHTML(replaceElements.bind(this)(containerGroup.consentPrefix))}` : nothing}
        ${containerGroup.consents.map((consent) => consentTemplate(consent, containerGroup))}
      </div>
    </tef-coms-collapsible>
  `;
}
// end oneclick text and container

// denyAll - OCW checkbox
function getWithdrawal(ocwGroup: ConsentGroupStore): TemplateResult {
  if (!ocwGroup.denyall || ocwGroup.oneclick) {
    return nothing as unknown as TemplateResult;
  }

  const checkboxId = `deny_check_${ocwGroup.id}`;

  return html`
    <div class="form-row">
      <div class="form-group-checkbox">
        <input
          id=${checkboxId}
          type="checkbox"
          .checked=${ocwGroup.areAllConsentsDenied}
          @click=${() => ocwGroup.denyAll()}
        />
        <label for=${checkboxId}>
          ${unsafeHTML(ocwGroup.denyall)}
        </label>
      </div>
    </div>
  `;
}

export function groupTemplate(this: TefComs4csMicroFrontend, group: ConsentGroupStore): TemplateResult {
  const consentTemplate = this.getTemplateFn('consentTemplate');

  let consentHtml: TemplateResult[] = [];
  if (group.oneclick) {
    consentHtml.push(getGroupOneclickConsentsContainer.bind(this)(group));
  } else {
    if (group.consentPrefix) {
      consentHtml.push(html`${unsafeHTML(replaceElements.bind(this)(group.consentPrefix))}`);
    }
    consentHtml = consentHtml.concat(group.consents.map((consent) => consentTemplate(consent, group)));
  }

  return html`
  <div class="section" data-testid="group-${group.name?.toLowerCase()}">
    <h4 class="typography-display--4">
      ${unsafeHTML(replaceElements.bind(this)(group.header))}
    </h4>
    ${group.oneclick ? html`${unsafeHTML(replaceElements.bind(this)(group.text))}` : nothing}
    <p>
      ${group.oneclick /* eslint-disable @typescript-eslint/indent */
        ? getOneclickHeader.bind(this)(group)
        : getGroupText.bind(this)(group)
        /* eslint-enable @typescript-eslint/indent */}
    </p>
    <div>${consentHtml}</div>
    ${group.footer ? html`
      <p>
        ${unsafeHTML(replaceElements.bind(this)(group.footer))}
      </p>
    ` : nothing}
    ${getWithdrawal(group)}
  </div>
  <hr class="divider" />
`;
}

export function groupTemplateOverview(this: TefComs4csMicroFrontend, group: ConsentGroupStore): TemplateResult {
  const filteredConsents = group.consents
    .filter(({ granted = false }) => granted === true);

  const consentTemplateOverview = this.getTemplateFn('consentTemplateOverview');

  return html`
    <div class="section" data-testid="group-${group.name?.toLowerCase()}">
      <h4 class="typography-display--4">
        ${unsafeHTML(replaceElements.bind(this)(group.header))}
      </h4>
      <div>
        ${(filteredConsents.length === 0) /* eslint-disable @typescript-eslint/indent */
          ? html`
            <div class="coms-ocw">
              ${group.denyallView ?? 'es liegen keine Erlaubnisse / Einwilligungen vor'}
            </div>`
          : html`
            <div class="container-fluid">
              ${group.consents.map((consent) => (
                consent.granted ? consentTemplateOverview(consent) : nothing))}
            </div>`
        /* eslint-enable @typescript-eslint/indent */}
      </div>
    </div>
    <hr class="divider" />
  `;
}
